// ===== BEGIN FILE: src/hooks/useChatMessages.ts =====
/**
 * useChatMessages — Extracted chat logic engine
 *
 * Handles: sendMessage (with real-time chunk streaming), model detection,
 * streaming state, countdown → replaced by elapsed timer, session tracking,
 * localStorage pending message injection, and all existing prompt patterns.
 *
 * NOTE: ALL existing prompts are preserved:
 *   - Comprehensive "All Life Areas" analysis prompt
 *   - Life area overview prompt
 *   - SWOT/Quick-Access localStorage injection
 *   - initialQuestion auto-send (mobile flow)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { buildChatContext, detectLifeAreaFromMessage, type ChatSourceData, type ChatContextOptions } from '../utils/chatContext';
import { supabase } from '../utils/supabaseClient';
import { type LifeAreaKey } from '../utils/lifeAreas';

// ===== TYPES =====

export interface Message {
  id: string; // unique id for stable React keys
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  followUpSentAt?: string; // track if follow-up consumed a credit
}

export type ModelOption = 'auto' | 'gpt-5.4' | 'gpt-5.4-mini';

export interface UseChatMessagesOptions {
  contextData: ChatSourceData;
  sessionId?: string;
  selectedLifeArea: LifeAreaKey | null;
  canAskChatQuestion: (type: 'general' | 'life_area') => boolean;
  getRemainingQuota: (type: 'charts' | 'chat_general' | 'chat_life_area') => number;
  getUpgradeMessage: (feature: string) => string;
  initialQuestion?: string;
  userRole?: 'admin' | 'member';
}

export interface UseChatMessagesReturn {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  input: string;
  setInput: (val: string) => void;
  pending: boolean;
  streamingContent: string;
  error: string | null;
  setError: (e: string | null) => void;
  elapsedSeconds: number;
  selectedModel: ModelOption;
  setSelectedModel: (m: ModelOption) => void;
  detectedModel: string;
  showQuickQuestions: boolean;
  setShowQuickQuestions: (v: boolean) => void;
  activeSessionId: string | null;
  setActiveSessionId: React.Dispatch<React.SetStateAction<string | null>>;
  sendMessage: (messageText?: string, displayText?: string, modelOverride?: 'gpt-5.4' | 'gpt-5.4-mini') => Promise<void>;
  stopResponse: () => void;
  clearChat: () => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  handleSuggestion: (question: string) => void;
}

// ===== HOOK =====

export function useChatMessages({
  contextData,
  sessionId,
  selectedLifeArea,
  canAskChatQuestion,
  getUpgradeMessage,
  initialQuestion,
}: UseChatMessagesOptions): UseChatMessagesReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [selectedModel, setSelectedModel] = useState<ModelOption>('auto');
  const [detectedModel, setDetectedModel] = useState('');
  const [showQuickQuestions, setShowQuickQuestions] = useState(false);
  const [initialQuestionSent, setInitialQuestionSent] = useState(false);
  // Track the session ID returned by the edge function so it is reused across messages
  const [activeSessionId, setActiveSessionId] = useState<string | null>(sessionId || null);

  const isMountedRef = useRef(true);
  const elapsedTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const userStoppedRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // ── localStorage pending message injection (SWOT / Quick-Access buttons)
  useEffect(() => {
    const pendingMessage = localStorage.getItem('pendingChatMessage');
    if (pendingMessage) {
      localStorage.removeItem('pendingChatMessage');
      setInput(pendingMessage);
      setTimeout(() => sendMessage(pendingMessage), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-send initial question on mount (mobile flow)
  useEffect(() => {
    if (initialQuestion && !initialQuestionSent && !pending) {
      setInitialQuestionSent(true);
      setTimeout(() => sendMessage(initialQuestion), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuestion]);

  // ── Auto-detect appropriate model based on query complexity
  // Deep multi-factor synthesis → gpt-5.4; everything else → gpt-5.4-mini (default workhorse)
  const detectBestModel = useCallback((query: string): 'gpt-5.4' | 'gpt-5.4-mini' => {
    const complexPatterns = [
      /why.*because/i,
      /explain.*detailed/i,
      /analyze.*combination/i,
      /compare.*between/i,
      /what.*if.*would/i,
      /multiple.*factors/i,
      /complex.*relationship/i,
      /all life areas/i,
      /comprehensive/i
    ];
    if (complexPatterns.some(p => p.test(query))) return 'gpt-5.4';
    return 'gpt-5.4-mini';
  }, []);

  // ── Stop/cancel ongoing response
  const stopResponse = useCallback(() => {
    if (abortControllerRef.current) {
      userStoppedRef.current = true;
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    setPending(false);
    setStreamingContent('');
    setElapsedSeconds(0);
  }, []);

  // ── Clear conversation
  const clearChat = useCallback(() => {
    if (window.confirm('Clear entire conversation?')) {
      setMessages([]);
      setError(null);
      setActiveSessionId(sessionId || null);
    }
  }, [sessionId]);

  // ── Send message (ALL existing prompts preserved, streaming fixed)
  const sendMessage = useCallback(async (messageText?: string, displayText?: string, modelOverride?: 'gpt-5.4' | 'gpt-5.4-mini') => {
    const textToSend = messageText || input.trim();
    if (!textToSend) return;

    const questionType = selectedLifeArea ? 'life_area' : 'general';
    if (!canAskChatQuestion(questionType)) {
      const upgradeMsg = getUpgradeMessage('chat');
      setError(`Chat limit reached! ${upgradeMsg}`);
      return;
    }

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: displayText || textToSend,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setPending(true);
    setError(null);
    setStreamingContent('');
    userStoppedRef.current = false;

    // Model selection: explicit override > user chip selection > auto-detection
    let modelToUse: 'gpt-5.4' | 'gpt-5.4-mini';
    if (modelOverride) {
      modelToUse = modelOverride;
      setDetectedModel('');
    } else if (selectedModel === 'auto') {
      modelToUse = detectBestModel(textToSend);
      setDetectedModel(modelToUse);
    } else {
      modelToUse = selectedModel;
      setDetectedModel('');
    }

    // Start elapsed timer (replaces countdown — shows "Xs" elapsed, not a countdown)
    setElapsedSeconds(0);
    elapsedTimerRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    try {
      // Build context with life area filter (preserved exactly)
      const autoDetectedLifeArea = selectedLifeArea || detectLifeAreaFromMessage(textToSend) || undefined;
      const contextOpts: ChatContextOptions = {
        includeFeeds: true,
        includeInterpretations: true,
        maxInterpretationLength: 500,
        lifeArea: autoDetectedLifeArea,
        includeVargas: true
      };
      const context = buildChatContext(contextData, contextOpts);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated. Please sign in to use chat.');

      abortControllerRef.current = new AbortController();
      // Auto-abort after 60 seconds to prevent indefinite hanging
      const timeoutId = setTimeout(() => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      }, 60000);

      let response: Response;
      try {
        // History truncation: send at most the last 8 prior messages, with older
        // assistant narratives trimmed — keeps long sessions within budget.
        const historyWindow = [...messages].slice(-8).map((m, idx, arr) => ({
          role: m.role,
          content: m.role === 'assistant' && idx < arr.length - 2 && m.content.length > 600
            ? m.content.slice(0, 600) + ' …[truncated]'
            : m.content
        }));
        response = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/chat`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              // NOTE: send the FULL prompt (textToSend) to the API — userMsg.content may be
              // a short display label when displayText is provided (UI-only).
              messages: [...historyWindow, { role: 'user', content: textToSend }],
              context,
              sessionId: activeSessionId || sessionId,
              model: modelToUse,
              questionType: selectedLifeArea ? 'life_area' : 'general',
              surface: 'chat'
            }),
            signal: abortControllerRef.current.signal
          }
        );
      } finally {
        clearTimeout(timeoutId);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Handle server-side quota exceeded
        if (response.status === 403 && errorData.error === 'quota_exceeded') {
          throw new Error(`Chat limit reached! ${errorData.message || getUpgradeMessage('chat')}`);
        }
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Capture session_id returned by edge function
      const returnedSessionId = response.headers.get('x-session-id');
      if (returnedSessionId && isMountedRef.current) {
        setActiveSessionId(returnedSessionId);
      }

      const contentType = response.headers.get('content-type') || '';
      let fullContent = '';

      if (contentType.includes('text/event-stream')) {
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        if (!reader) throw new Error('No response body available');

        // ── FIXED: Real-time chunk streaming (no word-by-word delay)
        let lineBuffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          lineBuffer += decoder.decode(value, { stream: true });
          const lines = lineBuffer.split('\n');
          lineBuffer = lines.pop() ?? '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const parsed = JSON.parse(line.slice(6));
                if (parsed.content) {
                  fullContent += parsed.content;
                  // Append each chunk immediately to streaming display
                  if (isMountedRef.current) {
                    setStreamingContent(prev => prev + parsed.content);
                  }
                }
                // Capture session id from SSE if embedded
                if (parsed.session_id && isMountedRef.current) {
                  setActiveSessionId(parsed.session_id);
                }
              } catch {}
            }
          }
        }
        // Handle any remaining buffered line
        if (lineBuffer.startsWith('data: ')) {
          try {
            const p = JSON.parse(lineBuffer.slice(6));
            if (p.content) {
              fullContent += p.content;
              if (isMountedRef.current) setStreamingContent(prev => prev + p.content);
            }
          } catch {}
        }

        if (!fullContent) throw new Error('No content received from stream');

      } else {
        // Fallback: non-streaming (backward compatibility)
        const result = await response.json();
        if (!result.answer) throw new Error('No answer received from AI');
        fullContent = result.answer;
        if (result.session_id && isMountedRef.current) setActiveSessionId(result.session_id);
      }

      // Finalize: move streaming content to messages array
      if (isMountedRef.current) {
        const assistantMsg: Message = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: fullContent,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, assistantMsg]);
        setStreamingContent('');
      }

    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Check if this was a timeout (auto-abort) or user-triggered stop
        const isTimeout = abortControllerRef.current === null || 
          (err.message && err.message.includes('timeout'));
        if (!isTimeout) {
          // User manually stopped — silently exit
          return;
        }
        if (userStoppedRef.current) {
          // User manually stopped — silently exit
          userStoppedRef.current = false;
          return;
        }
        // Timeout — show friendly message
        if (isMountedRef.current) {
          setError('The response took too long. Please try a shorter question or try again.');
          const errorMsg: Message = {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: `⏱️ The response took too long (over 60 seconds). Please try asking a shorter question, or try again.`,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, errorMsg]);
        }
        return;
      }
      console.error('Chat error:', err);
      if (isMountedRef.current) {
        const errText = err instanceof Error ? err.message : 'Failed to get response from AI';
        setError(errText);
        const errorMsg: Message = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: `⚠️ ${errText}. Please try again.`,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMsg]);
      }
    } finally {
      if (isMountedRef.current) {
        setPending(false);
        if (elapsedTimerRef.current) {
          clearInterval(elapsedTimerRef.current);
          elapsedTimerRef.current = null;
        }
        setElapsedSeconds(0);
      }
    }
  }, [input, messages, contextData, sessionId, activeSessionId, selectedLifeArea, selectedModel, canAskChatQuestion, getUpgradeMessage, detectBestModel]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  const handleSuggestion = useCallback((question: string) => {
    setInput(question);
    sendMessage(question);
  }, [sendMessage]);

  return {
    messages, setMessages,
    input, setInput,
    pending,
    streamingContent,
    error, setError,
    elapsedSeconds,
    selectedModel, setSelectedModel,
    detectedModel,
    showQuickQuestions, setShowQuickQuestions,
    activeSessionId, setActiveSessionId,
    sendMessage,
    stopResponse,
    clearChat,
    handleKeyDown,
    handleSuggestion
  };
}
// ===== END FILE: src/hooks/useChatMessages.ts =====
